import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  Search,
  Cpu,
  Package,
  Shield,
  Server,
  Building2,
  PenTool,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConsultantChatGate from '../components/ConsultantChatGate';
import SEOHead from '../components/SEOHead';
import TaskCTA from '../components/TaskCTA';
import RequestCalculationForm from '../components/RequestCalculationForm';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { PAGE_SEO } from '../data/pageSeo';

const SERVICE_ICONS = {
  Bot,
  Search,
  Cpu,
  Package,
  Shield,
  Server,
  Building2,
  PenTool,
};

function ServiceBlock({ service, index, accent, onCtaClick }) {
  const Icon = SERVICE_ICONS[service.icon] || Bot;
  const isReversed = index % 2 === 1;

  return (
    <section
      id={service.id}
      className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 scroll-mt-24 ${
        accent ? 'bg-accent/5 border-y border-accent/20' : ''
      }`}
    >
      <div
        className={`grid lg:grid-cols-2 gap-8 items-start ${
          isReversed ? 'lg:[&>*:first-child]:order-2' : ''
        }`}
      >
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                accent ? 'bg-accent/15' : 'bg-accent/10'
              }`}
            >
              <Icon className="w-5 h-5 text-accent" />
            </div>
            <p className="text-sm font-medium text-accent">{service.cardTitle}</p>
          </div>
          <h2 className="text-xl md:text-2xl font-bold mb-3">{service.h2}</h2>
          <p className="text-muted-foreground mb-3 leading-relaxed">{service.lead}</p>
          <p className="text-foreground mb-4 text-sm leading-relaxed">{service.summary}</p>

          <h3 className="font-semibold text-sm mb-2">Что делаем</h3>
          <ul className="space-y-1.5 mb-4">
            {service.steps.map((step) => (
              <li key={step} className="flex gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>{step}</span>
              </li>
            ))}
          </ul>

          {service.delivery && (
            <p className="text-sm mb-1">
              <span className="font-medium text-foreground">Форматы: </span>
              {service.delivery}
            </p>
          )}
          <p className="text-sm text-muted-foreground mb-4">
            <span className="font-medium text-foreground">Сроки: </span>
            {service.timeline}
          </p>

          <Card className="bg-muted/30 border-border/50 mb-4">
            <CardContent className="p-3 text-xs text-muted-foreground leading-relaxed">
              {service.citation}
            </CardContent>
          </Card>

          <Button
            className="bg-accent hover:bg-accent/90 text-accent-foreground"
            onClick={onCtaClick}
          >
            {service.cta}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl overflow-hidden border border-border/50 bg-accent/5 aspect-[4/3]">
            <img
              src={service.image}
              alt={service.imageAlt || ''}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-border/50 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-2 font-semibold">Сценарий</th>
                  <th className="text-left p-2 font-semibold">Результат</th>
                </tr>
              </thead>
              <tbody>
                {service.scenarios.map((row) => (
                  <tr key={row.name} className="border-t border-border/50">
                    <td className="p-2 font-medium">{row.name}</td>
                    <td className="p-2 text-muted-foreground">{row.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServiceLandingPage({
  seoPath,
  pageData,
  buildStructuredData,
}) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const seo = PAGE_SEO[seoPath];
  const {
    hero,
    tldr,
    intro,
    servicesSection,
    services,
    servicesAccent,
    whyUs,
    process,
    ecosystem,
    relatedPage,
    faq,
  } = pageData;

  const openForm = () => setIsFormOpen(true);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        keywords={seo.keywords}
        structuredData={buildStructuredData()}
      />
      <Header />

      <main className="flex-1 pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 relative">
            {hero.eyebrow && (
              <p className="text-sm font-semibold text-accent mb-3 uppercase tracking-wide">
                {hero.eyebrow}
              </p>
            )}
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-foreground max-w-4xl">
              {hero.h1}
            </h1>
            <p className="text-base text-muted-foreground max-w-3xl leading-relaxed mb-6">
              {hero.subtitle}
            </p>
            <Button
              className="bg-accent hover:bg-accent/90 text-accent-foreground h-11 px-6"
              onClick={openForm}
            >
              Обсудить проект
            </Button>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <Card className="bg-accent/5 border-accent/20">
            <CardContent className="p-5 md:p-6">
              <p className="text-sm font-semibold text-accent mb-2 uppercase tracking-wide">
                Кратко
              </p>
              <p className="text-foreground text-sm leading-relaxed">{tldr}</p>
            </CardContent>
          </Card>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-border/50">
          <h2 className="text-xl md:text-2xl font-bold mb-3">{intro.title}</h2>
          <p className="text-muted-foreground max-w-3xl mb-6 text-sm leading-relaxed">
            {intro.text}
          </p>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {intro.formats.map((format) => (
              <Card key={format.title} className="border-border/50">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-sm mb-1">{format.title}</h3>
                  <p className="text-xs text-muted-foreground mb-2">{format.what}</p>
                  <p className="text-xs text-foreground">{format.when}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-muted-foreground max-w-3xl text-sm">{intro.note}</p>
        </section>

        <div>
          {servicesSection && (
            <div
              className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-4 ${
                servicesAccent ? 'border-t border-accent/30' : 'border-t border-border/50'
              }`}
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-2">{servicesSection.title}</h2>
              <p className="text-muted-foreground text-sm max-w-2xl">{servicesSection.subtitle}</p>
            </div>
          )}
          {services.map((service, idx) => (
            <ServiceBlock
              key={service.id}
              service={service}
              index={idx}
              accent={servicesAccent}
              onCtaClick={openForm}
            />
          ))}
        </div>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-border/50">
          <h2 className="text-xl md:text-2xl font-bold mb-6">{whyUs.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {whyUs.rows.map((row) => (
              <Card key={row.title} className="border-border/50">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-sm mb-1">{row.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{row.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-border/50">
          <h2 className="text-xl md:text-2xl font-bold mb-6">{process.title}</h2>
          <div className="grid md:grid-cols-5 gap-3">
            {process.steps.map((step, idx) => (
              <Card key={step.title} className="border-border/50">
                <CardContent className="p-4">
                  <p className="text-xl font-bold text-accent mb-1">{idx + 1}</p>
                  <h3 className="font-semibold mb-1 text-sm">{step.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{step.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {ecosystem && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-border/50">
            <h2 className="text-xl md:text-2xl font-bold mb-3">{ecosystem.title}</h2>
            <p className="text-muted-foreground text-sm mb-4">{ecosystem.intro}</p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm border border-border/50 rounded-lg overflow-hidden max-w-3xl">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left p-2 font-semibold">Слой</th>
                    <th className="text-left p-2 font-semibold">Роль</th>
                  </tr>
                </thead>
                <tbody>
                  {ecosystem.layers.map((row) => (
                    <tr key={row.layer} className="border-t border-border/50">
                      <td className="p-2 font-medium">{row.layer}</td>
                      <td className="p-2 text-muted-foreground">{row.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground max-w-3xl text-sm mb-4 leading-relaxed">
              {ecosystem.example}
            </p>
            <Button
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
              onClick={openForm}
            >
              Обсудить комплексный проект
            </Button>
          </section>
        )}

        {relatedPage && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-border/50">
            <Card className="border-accent/20 bg-accent/5">
              <CardContent className="p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-accent mb-1 uppercase tracking-wide">
                    См. также
                  </p>
                  <h2 className="text-lg font-bold mb-1">{relatedPage.label}</h2>
                  <p className="text-sm text-muted-foreground">{relatedPage.text}</p>
                </div>
                <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground shrink-0">
                  <Link to={relatedPage.href}>
                    Перейти
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </section>
        )}

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-border/50">
          <h2 className="text-xl md:text-2xl font-bold mb-6">Частые вопросы</h2>
          <Accordion type="single" collapsible className="w-full">
            {faq.map((item, idx) => (
              <AccordionItem key={item.q} value={`faq-${idx}`}>
                <AccordionTrigger className="text-left text-sm">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                  {item.linkCbr ? (
                    <>
                      {item.a.split('ЦБР')[0]}
                      <Link to="/cbr" className="text-accent hover:underline">
                        ЦБР
                      </Link>
                      {item.a.split('ЦБР')[1]}
                    </>
                  ) : (
                    item.a
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <TaskCTA
          title="Обсудите задачу с экспертом!"
          onCalculationClick={openForm}
          showRequestButton={false}
        />
      </main>

      <Footer />
      <ConsultantChatGate />
      <RequestCalculationForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        calculatorData={null}
      />
    </div>
  );
}
